import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Typography, Box } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

export interface PreferenceItem {
  id: number;
  data: string; // ISO yyyy-mm-dd
  turno?: string;
}

interface PreferenceLimitDialogProps {
  open: boolean;
  preferences: PreferenceItem[];
  onDelete: (id: number) => Promise<void> | void;
  onClose: () => void;
}

const PreferenceLimitDialog: React.FC<PreferenceLimitDialogProps> = ({ open, preferences, onDelete, onClose }) => {
  const formatDate = (dateIso: string) => {
    const d = dayjs(dateIso);
    const dayName = d.format('dddd');
    const cap = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return `${cap} ${d.format('DD/MM/YYYY')}`;
  };

  const shown = preferences
    .slice() // copy
    .sort((a, b) => a.data.localeCompare(b.data))
    .slice(0, 2); // ensure max 2 shown

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Hai già selezionato 2 preferenze per questa settimana</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Cancella una preferenza per poter inserirne un'altra.
        </Typography>
        {shown.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nessuna preferenza trovata per questa settimana.</Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {shown.map((pref) => (
              <ListItem 
                key={pref.id} 
                divider
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 1.5,
                  px: 0
                }}
              >
                <ListItemText
                  primary={formatDate(pref.data)}
                  secondary={pref.turno ? `Turno: ${pref.turno}` : undefined}
                  sx={{ 
                    textAlign: 'left',
                    m: 0,
                    flex: '1 1 auto',
                    minWidth: 0,
                    pr: 1,
                    '& .MuiListItemText-primary': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    },
                    '& .MuiListItemText-secondary': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={async () => {
                    await onDelete(pref.id);
                  }}
                  sx={{ 
                    flexShrink: 0,
                    minWidth: 'auto',
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Cancella
                </Button>
              </ListItem>
            ))}
          </List>
        )}
        {preferences.length > 2 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Sono presenti più di 2 preferenze; ne vengono mostrate le prime due.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreferenceLimitDialog;
